import { useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { H2 } from "~/common/components";
import Select from "~/common/components/ui/select/Select";
import TextArea from "~/common/components/ui/Textarea";
import { generateYearOptions, monthsOptions } from "~/common/constants";
import {
  CompletedIndicatedTakenMedicationsAnswer,
  IndicatedTakenMedicationsAnswer,
  IndicatedTakenMedicationsQuestionData,
} from "~/common/utils/consultations/intake/interfaces";
import { ConsultationQuestionsLayout } from "~/patients/components/consultations/shared";
import { TEXT_MAX_LENGTH, TEXT_MEDIUM_LENGTH } from "~/patients/utils";
import {
  indicatedHaveTakenPsychMedicationsFormSchema,
  IndicatedHaveTakenPsychMedicationsFormValues,
} from "~/patients/utils/consultations/intakes";
import { formatPastMedicationCompletedAnswer } from "~/patients/utils/consultations/intakes/formatMedicationCompletedAnswer";
import { formatDateToBE } from "~/providers/utils";

export interface IndicatedHaveTakenPsychMedicationsFormProps {
  question: IndicatedTakenMedicationsQuestionData;
  handleGoBack: () => void;
  onSubmit: (answer: IndicatedTakenMedicationsAnswer) => void;
  isSubmitting: boolean;
  disableBack: boolean;
  currentQuestionOptions?: string[];
  completedAnswer?: CompletedIndicatedTakenMedicationsAnswer;
}

const BASE_MED_PATH = "answer.medications_data";

export const IndicatedHaveTakenPsychMedicationsForm = ({
  question,
  onSubmit,
  handleGoBack,
  isSubmitting,
  disableBack,
  currentQuestionOptions,
  completedAnswer,
}: IndicatedHaveTakenPsychMedicationsFormProps) => {
  const { title, subtitle } = question;

  const {
    handleSubmit,
    watch,
    register,
    control,
    formState: { errors },
  } = useForm<IndicatedHaveTakenPsychMedicationsFormValues>({
    resolver: zodResolver(indicatedHaveTakenPsychMedicationsFormSchema),
    defaultValues: {
      answer: {
        medications_data: formatPastMedicationCompletedAnswer(completedAnswer),
      },
    },
  });

  const saveAnswers: SubmitHandler<
    IndicatedHaveTakenPsychMedicationsFormValues
  > = (data) => {
    const medicationsWithMedication = data.answer.medications_data.map(
      (medicationData, index) => ({
        how_did_it_work: medicationData.how_did_it_work,
        date_started: formatDateToBE(
          medicationData.month_started,
          medicationData.year_started,
        ),
        date_stopped: formatDateToBE(
          medicationData.month_stopped,
          medicationData.year_stopped,
        ),
        why_it_stopped: medicationData.why_it_stopped,
        medication: currentQuestionOptions ? currentQuestionOptions[index] : "",
      }),
    );

    onSubmit({ answer: { medications_data: medicationsWithMedication } });
  };

  const medications = watch(BASE_MED_PATH) || [];

  const yearOptions = useMemo(() => generateYearOptions(), []);

  return (
    <ConsultationQuestionsLayout
      handleNext={handleSubmit(saveAnswers)}
      handleBack={handleGoBack}
      disableNext={!medications.length}
      isSubmitting={isSubmitting}
      title={title}
      subtitle={subtitle}
      disableBack={disableBack}
    >
      {currentQuestionOptions?.map((item, index) => (
        <div
          key={item}
          className={twMerge(
            "flex w-full flex-col items-start gap-4 border-neutral-700 pb-8",
            index !== medications.length - 1 && "border-b",
          )}
        >
          <H2>{item}</H2>
          <TextArea
            rows={3}
            id={`${BASE_MED_PATH}.${index}.how_did_it_work`}
            label="How did this medication work for you?"
            placeholder="Please describe (e.g side effects)."
            {...register(`${BASE_MED_PATH}.${index}.how_did_it_work`)}
            error={
              errors?.answer?.medications_data?.[index]?.how_did_it_work
                ?.message
            }
            charactersCount={
              watch(`${BASE_MED_PATH}.${index}.how_did_it_work`)?.length
            }
            className="border border-gray-20 placeholder:not-italic"
            maxCharacters={TEXT_MAX_LENGTH}
          />
          <div className="flex w-full gap-2">
            <Select
              error={
                errors?.answer?.medications_data?.[index]?.month_started
                  ?.message
              }
              label="Start"
              placeholder={"Select month"}
              id={`${BASE_MED_PATH}.${index}.month_started`}
              options={monthsOptions}
              control={control}
            />
            <div className="flex w-full flex-col justify-end">
              <Select
                error={
                  errors?.answer?.medications_data?.[index]?.month_started
                    ?.message
                }
                placeholder={"Select year"}
                id={`${BASE_MED_PATH}.${index}.year_started`}
                options={yearOptions}
                control={control}
              />
            </div>
          </div>

          <div className="flex w-full items-start gap-2">
            <Select
              label="Stopped"
              placeholder={"Select month"}
              id={`${BASE_MED_PATH}.${index}.month_stopped`}
              options={monthsOptions}
              control={control}
              error={
                errors?.answer?.medications_data?.[index]?.month_stopped
                  ?.message
              }
            />
            <div className="flex w-full flex-col justify-end">
              <Select
                containerClassName="mt-7"
                placeholder={"Select year"}
                id={`${BASE_MED_PATH}.${index}.year_stopped`}
                options={yearOptions}
                control={control}
                error={
                  errors?.answer?.medications_data?.[index]?.year_stopped
                    ?.message
                }
              />
            </div>
          </div>

          <TextArea
            rows={3}
            id={`${BASE_MED_PATH}.${index}.why_it_stopped`}
            label="Why did you stop taking this medication?"
            placeholder="Enter reason why you stopped taking this medication."
            {...register(`${BASE_MED_PATH}.${index}.why_it_stopped`)}
            error={
              errors?.answer?.medications_data?.[index]?.why_it_stopped?.message
            }
            charactersCount={
              watch(`${BASE_MED_PATH}.${index}.why_it_stopped`)?.length
            }
            maxCharacters={TEXT_MEDIUM_LENGTH}
            className="border border-gray-20 placeholder:not-italic"
          />
        </div>
      ))}
    </ConsultationQuestionsLayout>
  );
};
